<template>
  <div class="user text-dark">
    <Loader style="margin-top: 200px" v-if="!user && !calendar"></Loader>
    <div class="sub-header" v-if="isSubHeader">
      <p class="sub-header__title">
        {{ theSelectedUser.name }}
        <span class="sub-header__title-divider icon-arrow-right4"></span>
      </p>
      <a @click.prevent="setTheView('calendar')"
         :class="{'active': theView === 'calendar'}">{{ $t('vue2.common.calendar') }}</a>
      <a @click.prevent="setTheView('tasks')"
         :class="{'active': theView === 'tasks'}">{{ $t('vue2.task_overview.task_overview') }}</a>
    </div>
    <transition name="fadeInUp">
      <UserTasks
        class="marg-1-top"
        v-if="user && calendar && theView === 'tasks'"
        :isCanSeeOtherUsers="false"
        :user_id="theSelectedUser.userId"
        :user_calendar_id="theSelectedUser.calendarId"
        :user_name="theSelectedUser.name">
      </UserTasks>
    </transition>
    <transition name="fadeInUp">
      <div v-if="user && calendar && theView === 'calendar'">
        <week-selector
          :startDate="weekSelectorStartDate"
          :endDate="weekSelectorEndDate"
          :date="activeCalendarDate"
          @changed="setDate">
        </week-selector>
        <div id="printCalendar">
          <div class="user-calendar">
            <div class="user-calendar__header">
              <div class="user-calendar__header__left">
                <SelectCalendar
                  class="marg-1-right"
                  :isMultiple="userHasPermission('can_see_other_calendars')">
                </SelectCalendar>
                <UserPresence v-if="isUsersCalendar"
                              :calid="user.user_calendar_id"
                              :activeCalendarDate="activeCalendarDate"
                              @saved="presenceCall">
                </UserPresence>
              </div>
              <div class="user-calendar__header__right">
                <button class="no-print" @click="print"><span class="icon-printer3"></span></button>
                <button @click="today"
                        class="no-print"
                        :class="{'bttn--disabled': isNotThisWeek}">
                  {{ $t('repitition.today') }}
                </button>
                <button class="icon-arrow-left4 no-print" @click="prevWeek"></button>
                <button class="icon-arrow-right4 no-print" @click="nextWeek"></button>
              </div>
            </div>
            <div class="d-calendar__calendar__container"
                 :class="{'d-calendar__calendar__container--print-mode': isCalendarInPrintMode}">
              <DCalendar
                :isEditable="isEditable"
                :isCalendarInPrintMode="isCalendarInPrintMode"
                :isShowWeekends="isShowWeekends"
                :startTime="startTime"
                :endTime="endTime"
                :date="activeCalendarDate"
                :events="dCalendarEvents"
                :calendarId="calendar.id"
                :calendarName="calendar.name"
                :isEventsClickable="true"
                :isLoadingEvents="isEventsLoading"
                @onEventClick="setActiveEvent">
              </DCalendar>
            </div>
            <div class="user-calendar__footer no-print">
              <ExportCalendar
                v-if="isUsersCalendar"
                :calLink="iCalUrl">
              </ExportCalendar>
            </div>
          </div>
          <DWeekplan
            v-if="isShowWeekPlan"
            :isInPrintMode="isInPrintMode"
            :isReadOnly="isInPrintMode || !userHasPermission('can_edit_own_notes')">
          </DWeekplan>
        </div>
      </div>
    </transition>
    <transition name="zoomIn">
      <DCalendarShowEvent
        v-if="activeEvent"
        :canEditEvent="isEditable"
        :canEditNotes="userHasPermission('can_edit_own_notes')"
        :event="activeEvent"
        :calendar="calendar"
        :initCalendarId="calendar.id"
        :isInDCalendar="true"
        @updated="readEvents"
        @close="closeShowEvent">
      </DCalendarShowEvent>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import UserPresence from '../Presence/User/PresenceUser'
import Spinner from '../common/Spinner'
import ExportCalendar from '../ExportCalendar/ExportCalendar'
import DWeekplan from '../Weekplan/DWeekplan'
import weekSelector from '../datepicker/WeekSelecter.vue'
import Loader from '../common/Loader'
import DCalendarShowEvent from '../DCalendar/DCalendarShowEvent'
import DCalendar from '../DCalendar/DCalendar'
import UserTasks from '../User/UserTasks'
import SelectCalendar from '../common/SelectCalendar'

export default {
  components: {
    DCalendarShowEvent,
    SelectCalendar,
    UserTasks,
    DWeekplan,
    DCalendar,
    Loader,
    Spinner,
    UserPresence,
    ExportCalendar,
    weekSelector
  },
  data () {
    return {
      isShowWeekends: false,
      startTime: '06:00:00',
      endTime: '16:00:00',
      isInPrintMode: false,
      isCalendarInPrintMode: false,
      theView: 'calendar'
    }
  },
  computed: {
    isEditable () {
      return this.$store.getters.isUserCanEditCalendar
    },
    isSubHeader () {
      let isShowSubHeader = false
      if (!this.user || !this.calendar) {
        return isShowSubHeader
      }
      if (this.userHasPermission('can_see_own_task_overview') && this.isUsersCalendar) {
        isShowSubHeader = true
      }
      if (this.userHasPermission('can_see_others_task_overviews') && this.calendar.type === 'user') {
        isShowSubHeader = true
      }
      return isShowSubHeader
    },
    theSelectedUser () {
      if (!this.calendar) {
        let userObj = {
          userId: this.user.id,
          name: this.user.name,
          calendarId: this.user.user_calendar_id
        }
        return userObj
      }
      let userObj = {
        userId: this.allUsersInCalIdHash[this.calendar.id] ? this.allUsersInCalIdHash[this.calendar.id].id : this.user.id,
        name: this.calendar.name,
        calendarId: this.calendar.id
      }
      return userObj
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    theActiveCalendarId () {
      return this.$store.state.calendar.calendarId
    },
    user () {
      return this.$store.state.user
    },
    isUsersCalendar () {
      return this.$store.state.calendar.calendarId ? this.theActiveCalendarId === this.user.user_calendar_id : true
    },
    isShowWeekPlan () {
      return (this.calendar && this.calendar.type === 'klass') || (this.calendar && this.calendar.type === 'user') || (this.calendar && this.calendar.type === 'other')
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    calendar () {
      return this.$store.getters.activeCalendar
    },
    events () {
      return this.$store.state.calendar.events
    },
    availabilityEvents () {
      return this.$store.state.calendar.availabilityEvents
    },
    dCalendarEvents () {
      return this.events.concat(this.availabilityEvents)
    },
    isEventsLoading () {
      return this.$store.state.calendar.isEventsLoading
    },
    activeCalendarDate () {
      return moment(this.$store.state.calendar.date).format('YYYY-MM-DD')
    },
    activeEvent () {
      return this.$store.getters.activeCalendarEvent
    },
    iCalUrl () {
      if (this.calendar && this.calendar.ical_url) {
        return window.location.origin + this.calendar.ical_url
      } else if (this.calendar && this.calendar.ical_link) {
        return window.location.origin + this.calendar.ical_link
      } else {
        return 'loading'
      }
    },
    isNotThisWeek () {
      return moment(this.$store.state.calendar.date).isoWeek() === moment().isoWeek()
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    weekSelectorStartDate () {
      let startDate = this.calendar && this.calendar.start_date ? this.calendar && this.calendar.start_date : moment().startOf('isoweek')
      let calType = this.calendar ? this.calendar.type : 'klass'
      if (this.activeSchoolYear) {
        if (calType === 'klass') {
          startDate = moment(this.activeSchoolYear.student_time_start).startOf('isoweek')
        } else {
          startDate = moment(this.activeSchoolYear.employee_time_start).startOf('isoweek')
        }
      }
      return startDate
    },
    weekSelectorEndDate () {
      let endDate = this.calendar && this.calendar.end_date ? this.calendar && this.calendar.end_date : moment().startOf('isoweek')
      let calType = this.calendar ? this.calendar.type : 'klass'
      if (this.activeSchoolYear) {
        if (calType === 'klass') {
          endDate = moment(this.activeSchoolYear.student_time_end).startOf('isoweek')
        } else {
          endDate = moment(this.activeSchoolYear.employee_time_end).startOf('isoweek')
        }
      }
      return endDate
    }
  },
  watch: {
    calendar () {
      if (this.calendar && this.calendar.view_state > -1) {
        const state = this.calendar.view_state * 1
        if (state === 0) {
          this.startTime = '06:00:00'
          this.endTime = '18:00:00'
          this.isShowWeekends = false
        } else if (state === 1) {
          this.startTime = '06:00:00'
          this.endTime = '18:00:00'
          this.isShowWeekends = true
        } else {
          this.startTime = '00:00:00'
          this.endTime = '23:00:00'
          this.isShowWeekends = true
        }
      }
    },
    user (val) {
      if (val) {
        this.readEvents()
        this.readCalendar()
      }
    },
    activeCalendarDate () {
      this.readEvents()
    },
    theActiveCalendarId (val) {
      if (!val) {
        return
      }
      this.readEvents()
      this.readCalendar()
    }
  },
  methods: {
    setTheView (view) {
      let hasTasksInUrl = window.location.href.indexOf('?tasks') > 0
      if (view === 'tasks' && !hasTasksInUrl) {
        window.location.href = window.location.href + '?tasks'
      }
      if (view === 'calendar' && hasTasksInUrl) {
        window.location.href = window.location.href.substr(0, window.location.href.indexOf('?tasks'))
      }
      this.theView = view
    },
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      } else {
        return true
      }
    },
    readCalendar () {
      if (!this.user) {
        return
      }
      if (!this.theActiveCalendarId) {
        this.$store.commit('setActiveCalendarId', this.user.user_calendar_id)
      }
      this.$store.dispatch('readCalendar', this.theActiveCalendarId)
    },
    presenceCall (arrOfPresenceInWeek) {
      // this.$store.commit('updateCalendarPresence', arrOfPresenceInWeek)
      setTimeout(_ => {
        this.readEvents()
      }, 0)
    },
    readEvents () {
      if (!this.user) {
        return
      }
      let calendarId = this.theActiveCalendarId ? this.theActiveCalendarId : this.user.user_calendar_id
      let obj = {
        calIds: [calendarId],
        start: moment(this.activeCalendarDate).isoWeekday(1).format('YYYY-MM-DD 00:00'),
        end: moment(this.activeCalendarDate).isoWeekday(7).endOf('day')
      }
      this.$store.dispatch('readCalendarEvents', obj)
    },
    prevWeek () {
      let newDate = moment(this.activeCalendarDate).subtract(7, 'days')
      this.setDate(newDate)
    },
    nextWeek () {
      let newDate = moment(this.activeCalendarDate).add(7, 'days')
      this.setDate(newDate)
    },
    today () {
      this.setDate(moment())
    },
    setDate (date) {
      this.$store.commit('setActiveCalendarDate', date)
      this.$helpers.setCookie('calendar_current_date', moment(date).format('YYYY-MM-DD'))
    },
    print () {
      this.isCalendarInPrintMode = true
      setTimeout(_ => {
        this.isInPrintMode = true
        this.$nextTick(_ => {
          // https://stackoverflow.com/questions/52343006/how-to-print-a-part-of-a-vue-component-without-losing-the-style
          const prtHtml = document.getElementById('printCalendar').innerHTML
          // Get all stylesheets HTML
          let stylesHtml = ''
          for (const node of [...document.querySelectorAll('link[rel="stylesheet"][href*="/app/static/css/"], style')]) {
            stylesHtml += node.outerHTML
          }
          // Open the print window
          const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')

          WinPrint.document.write(`<!DOCTYPE html><html><head>${stylesHtml}</head><body>${prtHtml}</body></html>`)
          var afterPrint = () => {
            WinPrint.close()
            this.isInPrintMode = false
            this.isCalendarInPrintMode = false
          }
          WinPrint.onafterprint = afterPrint
          setTimeout(_ => {
            WinPrint.document.close()
            WinPrint.focus()
            WinPrint.print()
          }, 1000)
        }, 500)
      })
    },
    setActiveEvent (event) {
      this.$store.commit('setActiveCalendarEventId', event.obj.event_id)
    },
    closeShowEvent () {
      this.$store.commit('setActiveCalendarEventId', null)
    },
    findRoute () {
      const queryString = window.location.href
      let calUrlIndex = window.location.href.indexOf('?cal=')
      if (calUrlIndex > -1) {
        let hashEnd = window.location.href.indexOf('#')
        let calId = queryString.substr(calUrlIndex + 5, hashEnd)
        this.$store.commit('setActiveCalendarId', calId)
      }
    },
    readUsers () {
      this.$store.dispatch('getUsers')
    }
  },
  beforeMount () {
    const cookieDate = this.$helpers.getCookie('calendar_current_date')
    if (cookieDate && moment(cookieDate).isValid()) {
      this.$store.commit('setActiveCalendarDate', cookieDate)
    }
    const theDevice = this.$helpers.checkUserDevice()
    if (theDevice === 'Mobile') {
      this.$store.commit('setIsDeviceMobile', true)
      document.querySelector('body').classList.add('device--is-mobile')
    }
  },
  mounted () {
    this.readEvents()
    this.readCalendar()
    this.findRoute()
    this.readUsers()
  }
}
</script>


<style scoped>

.user {
  margin: 0 1rem 1rem;
}

.user-calendar {
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
  background: #fff;
  overflow: hidden;
  border-radius: 4px;
  position: relative;
  padding: 1rem;
}

.user-calendar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.user-calendar__header__left {
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 100;
}

.user-calendar__header__right {
  display: flex;
  align-items: center;
}

.user-calendar__header__right button {
  margin-left: 0.5rem;
}

.user-calendar__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}


.aniZoomIn-enter-active {
  animation: aniZoomIn .25s;
}

.aniZoomIn-leave-active {
  animation: aniZoomIn .25s reverse;
}

@keyframes aniZoomIn {
  from {
    opacity: 0;
    transform: scale3d(.3, .3, .3);
  }

  50% {
    opacity: 1;
  }
}

.d-calendar__calendar__container {
  position: relative;
  height: calc(100vh - 144px);
  min-height: 650px;
}

.d-calendar__calendar__container.d-calendar__calendar__container--print-mode {
  height: 650px;
}

@media print {
  .d-calendar__calendar__container {
    page-break-after: always;
  }

  .d-calendar__calendar__container {
    height: 650px;
  }
}

.sub-header {
  height: 40px;
  display: flex;
  border-bottom: solid 1px #CCCCCC;
  padding: 0;
  margin: 0;
}

.sub-header__title,
.sub-header a {
  font-weight: bold;
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #3e4450;
  opacity: 0.7;
  text-transform: uppercase;
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;
}

.sub-header__title {
  margin-left: 0;
}

.sub-header a {
  margin: 0 20px;
  cursor: pointer;
}

.sub-header__title-divider {
  margin-left: 20px;
}

.sub-header a.active, .sub-header a:hover {
  opacity: 1;
  border-bottom: 2px solid #468AD7;
  transition: all 0.15s ease-in-out;
}

</style>



// WEBPACK FOOTER //
// CalendarView.vue?4d60367c